import {TZDate} from '@date-fns/tz'
import {format} from 'date-fns'

/** IANA timezone used for Depot's business calendar and clock presentation. */
export const BUSINESS_TIMEZONE = 'America/Chicago' as const

/** Parses an ISO timestamp while retaining the instant and exposing Chicago components. */
export function businessTimestampFromIso(value: string): TZDate {
    return new TZDate(value, BUSINESS_TIMEZONE)
}

/** Re-zones an existing instant to Chicago without changing its epoch milliseconds. */
export function businessTimestampFromDate(value: Date): TZDate {
    return new TZDate(value.getTime(), BUSINESS_TIMEZONE)
}

/** Parses a `YYYY-MM-DD` value as a calendar date without applying timezone conversion. */
export function businessDateOnlyFromIso(value: string): Date {
    const [yearPart, monthPart, dayPart] = value.split('-')
    const year = Number(yearPart)
    const month = Number(monthPart)
    const day = Number(dayPart)
    return new Date(year, month - 1, day)
}

/** Returns a browser-local Date carrying the current Chicago calendar date. */
export function businessCalendarDate(from = new Date()): Date {
    const current = businessTimestampFromDate(from)
    return new Date(current.getFullYear(), current.getMonth(), current.getDate())
}

/** Returns the first and last dates of the Chicago calendar month containing an instant. */
export function businessCalendarMonthRange(from = new Date()): {
    startDate: string
    endDate: string
} {
    const current = businessTimestampFromDate(from)
    const start = new TZDate(current.getFullYear(), current.getMonth(), 1, BUSINESS_TIMEZONE)
    const end = new TZDate(current.getFullYear(), current.getMonth() + 1, 0, BUSINESS_TIMEZONE)

    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
    }
}

/** Formats an instant using Chicago calendar and clock components. */
export function formatBusinessTimestamp(value: Date | string, pattern: string): string {
    const timestamp =
        typeof value === 'string'
            ? businessTimestampFromIso(value)
            : businessTimestampFromDate(value)
    return format(timestamp, pattern)
}
